// The docked recorder / player at the bottom of the composer (see
// useAudioDock). Non-modal: no scrim, no focus trap, the page stays readable
// and scrollable behind it. Desktop: a pill about as wide as the column,
// centred over it. Phones: a compact full-width bar above the safe area.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import type { Block } from '../../lib/types'
import { isPieceNarration } from '../../lib/narration'
import { formatDuration, formatLimit, NARRATION_MAX_SECONDS, VOICE_MEMO_MAX_SECONDS } from '../../lib/audio'
import { recorderMessage, recorderSupport } from '../../lib/recorder'
import { PLAYBACK_RATES } from '../../lib/useAudioPlayer'
import { blockText } from '../../lib/text'
import { useBackend } from '../../lib/session'
import { InlineConfirm, smallBtn } from '../../components/audio/InlineConfirm'
import { LevelMeter } from '../../components/audio/LevelMeter'
import { useAudioFileUpload } from '../../components/audio/useAudioFileUpload'
import { MicGlyph, PauseGlyph, PlayGlyph, StopGlyph } from '../../components/audio/icons'
import { Scrubber } from '../../components/audio/Waveform'
import type { AudioDockController } from './useAudioDock'
import type { DraftClip } from './recordingSession'

const LABELS = { record: 'Narration recorder', listen: 'Narration player', memo: 'Voice memo recorder' } as const

const roundBtn =
  'flex size-[34px] shrink-0 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50'

function snippetOf(blocks: Block[], id: string | null, max = 34): string {
  const b = id ? blocks.find((x) => x.id === id) : undefined
  const t = b ? blockText(b).trim() : ''
  return t.length > max ? `${t.slice(0, max - 1)}…` : t
}

/** "¶ 3 of 12" for a narrated block. */
function paraLabel(order: string[], id: string | null): string | null {
  if (!id) return null
  const i = order.indexOf(id)
  return i < 0 ? null : `¶ ${i + 1} of ${order.length}`
}

export function AudioDock({ dock, authorName }: { dock: AudioDockController; authorName: string }) {
  if (!dock.mode) return null
  return <DockFrame dock={dock} authorName={authorName} />
}

function DockFrame({ dock, authorName }: { dock: AudioDockController; authorName: string }) {
  const mode = dock.mode!
  const label = LABELS[mode]
  // Opening a mode brings keyboard focus (and a screen reader) to the dock, without a trap.
  const frame = useRef<HTMLElement>(null)
  // …and closing it hands focus back to what opened it (the card's button, say).
  const [opener] = useState(() => document.activeElement as HTMLElement | null)
  useEffect(() => {
    frame.current?.focus({ preventScroll: true })
  }, [mode])
  useEffect(
    () => () => {
      if (opener?.isConnected && opener !== document.body) opener.focus({ preventScroll: true })
    },
    [opener],
  )
  return (
    <section
      ref={frame}
      tabIndex={-1}
      data-audio-dock=""
      data-mode={mode}
      role="region"
      aria-label={label}
      className="sticky bottom-3 z-20 mt-8 flex flex-col gap-2 rounded-2xl border border-border/40 bg-card/95 px-3.5 py-3 font-sans outline-none shadow-[0_14px_44px_rgba(45,27,30,.18)] backdrop-blur-sm max-sm:bottom-0 max-sm:-mx-4 max-sm:gap-1.5 max-sm:rounded-none max-sm:rounded-t-[14px] max-sm:border-x-0 max-sm:border-b-0 max-sm:px-3 max-sm:pb-[max(10px,env(safe-area-inset-bottom))] max-sm:pt-2.5"
    >
      {dock.mode === 'listen' ? <ListenBody dock={dock} authorName={authorName} /> : <RecordBody dock={dock} />}
      {dock.hint && (
        <p role="status" className="m-0 text-[11.5px] font-medium text-muted-foreground">
          {dock.hint}
        </p>
      )}
      {dock.confirmExit && (
        <InlineConfirm
          message={
            dock.mode === 'memo'
              ? 'Discard this voice memo?'
              : `Discard ${plural(dock.session?.freshCount ?? 0, 'unsaved piece')}?`
          }
          detail="What you recorded here hasn’t been saved."
          confirmLabel="Discard"
          cancelLabel="Keep"
          focus="cancel"
          onConfirm={dock.exit}
          onCancel={dock.cancelExit}
        />
      )}
    </section>
  )
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/* ─────────────────────────────────────────────────────────── shared bits */

function FollowToggle({ dock }: { dock: AudioDockController }) {
  return (
    <button
      type="button"
      aria-pressed={dock.follow}
      onClick={() => dock.setFollow(!dock.follow)}
      title={dock.follow ? 'The page keeps the paragraph in view' : 'Bring the page back to the paragraph'}
      className={`${smallBtn.outline} gap-1 ${dock.follow ? 'border-accent bg-accent/15 text-primary' : ''}`}
    >
      <span aria-hidden className={`size-1.5 rounded-full ${dock.follow ? 'bg-primary' : 'bg-foreground/30'}`} />
      Follow
    </button>
  )
}

function LeaveButton({ dock, label = 'Leave' }: { dock: AudioDockController; label?: string }) {
  return (
    <button type="button" onClick={dock.requestExit} className={smallBtn.ghost} title="Esc">
      {label}
    </button>
  )
}

/** Mic problems, upload failures: inline, with a way on. */
function DockAlert({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg bg-accent/15 px-3 py-2 text-[13px]/[1.45] text-primary">
      <span className="min-w-0 flex-1">{children}</span>
      {action}
    </div>
  )
}

/** "Upload a file instead": a hidden input, not a sheet. Asks about marks when replacing a synced file. */
function FileFallback({ dock, maxSeconds, emphasis = false }: { dock: AudioDockController; maxSeconds: number; emphasis?: boolean }) {
  const input = useRef<HTMLInputElement>(null)
  const file = useAudioFileUpload(maxSeconds)
  const [pending, setPending] = useState<Parameters<AudioDockController['applyFile']>[0] | null>(null)
  const n = dock.narration
  const marked = dock.mode === 'record' && n && !isPieceNarration(n) ? Object.keys(n.marks ?? {}).length : 0

  const picked = async (f: File | undefined) => {
    const audio = await file.upload(f)
    if (!audio) return
    if (marked > 0) setPending(audio)
    else dock.applyFile(audio, false)
  }

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="audio/*"
        aria-label="Audio file"
        hidden
        onChange={(e) => {
          void picked(e.target.files?.[0])
          e.target.value = ''
        }}
      />
      <button
        type="button"
        disabled={!!file.busy}
        onClick={() => input.current?.click()}
        className={emphasis ? smallBtn.primary : smallBtn.ghost}
      >
        {file.busy ?? 'Upload a file instead'}
      </button>
      {file.error && (
        <span role="alert" className="basis-full text-[11.5px] font-medium text-primary">
          {file.error}
        </span>
      )}
      {pending && (
        <InlineConfirm
          className="basis-full"
          message={`Keep your ${plural(marked, 'paragraph mark')}?`}
          detail="They were timed to the old recording."
          confirmLabel="Keep marks"
          cancelLabel="Start fresh"
          onConfirm={() => dock.applyFile(pending, true)}
          onCancel={() => dock.applyFile(pending, false)}
          onDismiss={() => dock.applyFile(pending, true)}
        />
      )}
    </>
  )
}

/* ───────────────────────────────────────────────────── record and memo */

function RecordBody({ dock }: { dock: AudioDockController }) {
  const s = dock.session
  const backend = useBackend()
  const subscribe = useCallback((fn: (l: number) => void) => s?.onLevel(fn) ?? (() => {}), [s])
  if (!s) return null
  const memo = dock.mode === 'memo'
  const max = memo ? VOICE_MEMO_MAX_SECONDS : NARRATION_MAX_SECONDS
  const phase = s.phase
  const where = memo ? (dock.memo?.anchorId ? snippetOf(dock.blocks, dock.memo.anchorId) : '') : snippetOf(dock.blocks, s.currentId)
  const para = memo ? null : paraLabel(dock.order, s.currentId)
  const support = recorderSupport()
  const err = s.error
  const fallbackFirst = err?.code === 'insecure' || err?.code === 'unsupported' || (support !== 'ok' && phase === 'idle' && !err)

  let body: ReactNode
  if (phase === 'recording' || phase === 'starting' || phase === 'paused' || phase === 'finishing') {
    const live = phase === 'recording'
    const elapsed = s.elapsed()
    const nextId = memo ? null : nextNarrated(dock, s.currentId)
    const finishes = !nextId
    body = (
      <>
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex shrink-0 items-center gap-1.5 text-[12px] font-semibold tabular-nums text-primary" aria-live="off">
            <span aria-hidden className={`size-2 rounded-full bg-primary ${live ? 'animate-pulse' : 'opacity-40'}`} />
            {phase === 'starting' ? 'Starting…' : phase === 'finishing' ? 'Finishing…' : phase === 'paused' ? 'Paused' : formatDuration(elapsed)}
          </span>
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground/80">
            {memo ? (
              <>Voice memo{where ? <span className="text-muted-foreground"> · beside “{where}”</span> : null}</>
            ) : (
              <>
                {para}
                {where && <span className="text-muted-foreground max-sm:hidden"> · “{where}”</span>}
              </>
            )}
          </span>
          <LevelMeter subscribe={subscribe} bars={22} height={22} className="max-sm:hidden" />
          <LevelMeter subscribe={subscribe} bars={12} height={18} className="sm:hidden" />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {!memo && (
            <button
              type="button"
              onClick={() => s.next()}
              disabled={!live}
              aria-keyshortcuts="Space ArrowDown"
              className={`${smallBtn.primary} gap-1.5`}
            >
              {finishes ? 'Finish' : 'Next paragraph'}
              <kbd className="font-sans text-[10px] font-medium text-accent-foreground/70 max-sm:hidden">Space</kbd>
            </button>
          )}
          {phase === 'paused' ? (
            <button type="button" onClick={() => s.resume()} aria-keyshortcuts="P" className={smallBtn.outline}>
              Resume
            </button>
          ) : (
            !memo && (
              <button type="button" onClick={() => s.pause()} disabled={!live} aria-keyshortcuts="P" className={smallBtn.outline}>
                Pause
              </button>
            )
          )}
          <button
            type="button"
            onClick={() => void s.stop()}
            disabled={!(live || phase === 'paused')}
            aria-label="Stop"
            className={`${smallBtn.outline} gap-1.5 text-primary`}
          >
            <StopGlyph size={9} />
            Stop
          </button>
          <span className="ml-auto flex items-center gap-1.5">
            {!memo && <FollowToggle dock={dock} />}
            <span className="text-[11px] tabular-nums text-muted-foreground/70 max-sm:hidden">up to {formatLimit(max)}</span>
          </span>
        </div>
        {!memo && live && (
          <p className="m-0 text-[11px] text-muted-foreground max-sm:hidden">
            Space or ↓ starts the next paragraph’s piece · P pauses · tap a later paragraph to jump there
          </p>
        )}
      </>
    )
  } else if (phase === 'saving') {
    const up = s.uploading
    body = (
      <p role="status" className="m-0 py-1 text-[13px] font-medium text-muted-foreground">
        Saving… {up.total > 0 ? `uploaded ${up.done} of ${up.total}` : ''}
      </p>
    )
  } else if (phase === 'review' && s.clips.length > 0) {
    body = memo ? <MemoReview dock={dock} /> : <PiecesReview dock={dock} />
  } else {
    // Ready to record.
    body = (
      <>
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={dock.start}
            disabled={support !== 'ok'}
            aria-label="Start recording"
            className={`${roundBtn} bg-accent text-accent-foreground shadow-lg shadow-accent/30 hover:opacity-90`}
          >
            <MicGlyph size={16} />
          </button>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-[13px] font-medium text-foreground">
              {memo ? 'Record a voice memo' : isPieceNarration(dock.narration) ? 'Record from a paragraph' : 'Record it paragraph by paragraph'}
            </span>
            <span className="truncate text-[11.5px] font-medium text-muted-foreground">
              {memo
                ? `${where ? `Beside “${where}” · ` : ''}up to ${formatLimit(max)}`
                : s.currentId
                  ? `Starts at ${paraLabel(dock.order, s.currentId)}${where ? ` · “${where}”` : ''} · tap a paragraph to start elsewhere`
                  : 'Tap the paragraph to start at'}
            </span>
          </div>
        </div>
        {!memo && (
          <p className="m-0 text-[11.5px]/[1.45] text-muted-foreground">
            Each paragraph becomes its own piece: press <b className="font-semibold text-muted-foreground">Next</b> (or Space) as you start the next one, so you can redo any piece later.
            {dock.narration && !isPieceNarration(dock.narration) ? ' Saving replaces the current recording.' : ''}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <FileFallback dock={dock} maxSeconds={max} emphasis={fallbackFirst} />
          <span className="ml-auto">
            <LeaveButton dock={dock} label="Cancel" />
          </span>
        </div>
      </>
    )
  }

  return (
    <>
      {body}
      {err && (
        <DockAlert
          action={
            s.clips.some((c) => c.upload === 'failed') ? (
              <button type="button" onClick={() => s.retryUploads()} className="font-semibold underline underline-offset-2">
                Retry
              </button>
            ) : undefined
          }
        >
          {err.message}
        </DockAlert>
      )}
      {!err && support !== 'ok' && phase === 'idle' && <DockAlert>{recorderMessage(support)}</DockAlert>}
      {s.note && <p className="m-0 text-[11.5px] text-muted-foreground">{s.note}</p>}
      {backend.mode === 'local' && phase === 'idle' && (
        <p className="m-0 text-[10.5px]/[1.4] text-muted-foreground/70">Demo mode keeps audio in this browser, so each piece must be under 3 MB.</p>
      )}
    </>
  )
}

function nextNarrated(dock: AudioDockController, id: string | null): string | null {
  const s = dock.session
  if (!id) return null
  const i = dock.order.indexOf(id)
  const next = i >= 0 && i + 1 < dock.order.length ? dock.order[i + 1] : null
  const until = s?.range?.until
  if (next && until && dock.order.indexOf(next) >= dock.order.indexOf(until)) return null
  return next
}

/** After Stop: play all, the pieces, re-record one, record on, Discard, Save. */
function PiecesReview({ dock }: { dock: AudioDockController }) {
  const s = dock.session!
  const p = dock.review
  const [showList, setShowList] = useState(true)
  const current = s.currentId
  const hasPiece = !!current && s.clips.some((c) => c.blockId === current)
  const unsaved = s.unsaved
  const pieces = s.clips
  const total = pieces.reduce((t, c) => t + c.duration, 0)
  return (
    <>
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={p.toggle}
          aria-label={p.playing ? 'Pause' : 'Play all'}
          aria-keyshortcuts="K"
          className={`${roundBtn} bg-accent text-accent-foreground hover:opacity-90`}
        >
          {p.playing ? <PauseGlyph /> : <PlayGlyph />}
        </button>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-baseline justify-between gap-2 text-[13px] font-medium">
            <span className="truncate text-foreground">
              {plural(pieces.length, 'piece')}
              {unsaved && <span className="text-primary"> · not saved yet</span>}
            </span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {formatDuration(p.time)} / {formatDuration(p.duration || total)}
            </span>
          </div>
          <Scrubber time={p.time} duration={p.duration || total} onSeek={p.seek} label="Seek the recording" className="-my-1" />
        </div>
        <FollowToggle dock={dock} />
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-expanded={showList}
          onClick={() => setShowList((v) => !v)}
          className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground hover:text-foreground"
        >
          Pieces {showList ? '▾' : '▸'}
        </button>
        {p.error && <span className="text-[11px] text-primary">{p.error}</span>}
      </div>
      {showList && (
        <ol aria-label="Pieces" className="wr-chip-row m-0 flex list-none gap-1.5 p-0 pb-0.5">
          {pieces.map((c, i) => (
            <PieceChip key={c.id} clip={c} index={i} dock={dock} selected={c.blockId === current} />
          ))}
        </ol>
      )}

      <div className="flex flex-wrap items-center gap-1.5 border-t border-border/40 pt-2">
        {current ? (
          <>
            <span className="mr-1 min-w-0 max-w-full truncate text-[11.5px] font-medium text-muted-foreground">
              {paraLabel(dock.order, current)} · “{snippetOf(dock.blocks, current, 26)}”
            </span>
            {hasPiece && (
              <button type="button" onClick={dock.rerecordPiece} className={smallBtn.outline}>
                Re-record this piece
              </button>
            )}
            <button type="button" onClick={dock.recordFromHere} className={smallBtn.outline}>
              Record from here on
            </button>
          </>
        ) : (
          <span className="text-[11.5px] font-medium text-muted-foreground">Tap a paragraph to re-record its piece, or to record on from there.</span>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          <button type="button" onClick={dock.requestExit} className={smallBtn.ghost}>
            {unsaved ? 'Discard' : 'Close'}
          </button>
          <button type="button" onClick={() => void dock.save()} disabled={!unsaved} className={smallBtn.primary}>
            Save
          </button>
        </span>
      </div>
    </>
  )
}

function PieceChip({ clip, index, dock, selected }: { clip: DraftClip; index: number; dock: AudioDockController; selected: boolean }) {
  const para = dock.order.indexOf(clip.blockId)
  const where = para >= 0 ? `¶ ${para + 1}` : '¶ ?'
  const status = clip.upload === 'failed' ? ' · not uploaded' : clip.upload === 'uploading' ? ' · uploading' : ''
  return (
    <li className="shrink-0">
      <button
        type="button"
        onClick={() => dock.playPiece(clip.blockId)}
        aria-label={`Piece ${index + 1}, ${where}, ${formatDuration(clip.duration)}${status}`}
        aria-current={selected ? 'true' : undefined}
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11.5px] font-medium tabular-nums transition-colors ${
          selected ? 'border-accent bg-accent/15 text-primary' : 'border-border/60 text-muted-foreground hover:bg-foreground/5'
        }`}
      >
        <span aria-hidden className={`size-1.5 rounded-full ${clip.fresh ? 'bg-primary' : 'bg-accent'} ${clip.upload === 'failed' ? 'bg-foreground/30' : ''}`} />
        {where}
        <span className="text-muted-foreground">{formatDuration(clip.duration)}</span>
      </button>
    </li>
  )
}

/** A voice memo's one take: play it back, retake, or keep it. */
function MemoReview({ dock }: { dock: AudioDockController }) {
  const s = dock.session!
  const p = dock.review
  const take = s.clips[0]
  return (
    <>
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={p.toggle}
          aria-label={p.playing ? 'Pause playback' : 'Play back'}
          className={`${roundBtn} bg-accent text-accent-foreground hover:opacity-90`}
        >
          {p.playing ? <PauseGlyph /> : <PlayGlyph />}
        </button>
        <Scrubber time={p.time} duration={p.duration || take.duration} onSeek={p.seek} label="Seek the voice memo" className="min-w-0 flex-1" />
        <span className="shrink-0 text-[12px] font-medium tabular-nums text-muted-foreground">{formatDuration(take.duration)}</span>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        <button type="button" onClick={dock.requestExit} className={`${smallBtn.ghost} mr-auto`}>
          Discard
        </button>
        <button type="button" onClick={dock.start} className={smallBtn.outline}>
          Retake
        </button>
        <button type="button" onClick={() => void dock.save()} className={smallBtn.primary}>
          Use this
        </button>
      </div>
    </>
  )
}

/* ─────────────────────────────────────────────────────── listen and sync */

function ListenBody({ dock, authorName }: { dock: AudioDockController; authorName: string }) {
  const { sync } = dock
  const p = sync.player
  const n = dock.narration
  const duration = p.duration || n?.duration || 0
  const pieces = isPieceNarration(n)
  const nextRate = () => p.setRate(PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(p.rate) + 1) % PLAYBACK_RATES.length] ?? 1)
  return (
    <>
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={p.toggle}
          aria-label={p.playing ? 'Pause narration' : 'Play narration'}
          aria-keyshortcuts="K"
          className={`${roundBtn} bg-accent text-accent-foreground hover:opacity-90`}
        >
          {p.playing ? <PauseGlyph /> : <PlayGlyph />}
        </button>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-baseline justify-between gap-2 text-[13px] font-medium">
            <span className="truncate text-foreground">
              {dock.syncing ? 'Syncing' : `${authorName} reads this writing`}
              {sync.activeBlockId && <span className="text-muted-foreground"> · {paraLabel(dock.order, sync.activeBlockId)}</span>}
            </span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {formatDuration(p.time)} / {formatDuration(duration)}
            </span>
          </div>
          <Scrubber time={p.time} duration={duration} onSeek={p.seek} label="Seek narration" className="-my-1" />
        </div>
        <button
          type="button"
          onClick={nextRate}
          aria-label={`Playback speed ${p.rate}×`}
          className="shrink-0 rounded-full px-1.5 py-1 text-[12px] font-medium tabular-nums text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
        >
          {p.rate}×
        </button>
      </div>
      {p.error && <DockAlert>{p.error}</DockAlert>}
      {dock.syncing ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="min-w-0 flex-1 text-[11.5px]/[1.45] font-medium text-primary">
            Tap each paragraph as you start reading it, or press Space
            {dock.syncNext && <span className="text-muted-foreground"> · Space marks {paraLabel(dock.order, dock.syncNext)}</span>}
            <span className="text-muted-foreground">
              {' '}
              · {sync.markedCount} of {sync.narratedCount} marked
            </span>
          </span>
          {sync.markedCount > 0 && (
            <button type="button" onClick={sync.clearMarks} className={smallBtn.ghost}>
              Clear marks
            </button>
          )}
          <FollowToggle dock={dock} />
          <button type="button" onClick={dock.toggleSync} className={smallBtn.primary}>
            Done
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="min-w-0 flex-1 text-[11.5px] font-medium text-muted-foreground">
            {pieces
              ? 'Synced by pieces'
              : `${sync.markedCount > 0 ? `${sync.markedCount} of ${sync.narratedCount} paragraphs marked` : 'Paragraphs are timed by length'} · tap a paragraph to hear it`}
          </span>
          <FollowToggle dock={dock} />
          {!pieces && (
            <button
              type="button"
              aria-pressed={false}
              onClick={dock.toggleSync}
              disabled={sync.narratedCount === 0 || !(duration > 0)}
              title="Mark where each paragraph starts, so readers can follow along"
              className={smallBtn.outline}
            >
              Sync
            </button>
          )}
          <LeaveButton dock={dock} label="Done" />
        </div>
      )}
    </>
  )
}
